import { supabase } from './supabaseClient';
import type { Subscription, UserPlan } from '@/types/database';
import { Capacitor } from '@capacitor/core';

export type CancelSubscriptionResult = {
  ok: boolean;
  provider: 'paypal' | 'manual' | 'apple' | 'google';
  code?: string;
  message?: string;
  current_period_end?: string | null;
};

export type MobileStorePlatform = 'ios' | 'android';
export type MobileStoreStartResult = {
  platform: MobileStorePlatform;
  mode: 'native-plugin' | 'store-link';
  targetUrl?: string;
};

type DynamicPurchasePlugin = {
  subscribe?: (payload?: Record<string, unknown>) => Promise<unknown>;
  purchase?: (payload?: Record<string, unknown>) => Promise<unknown>;
  buy?: (payload?: Record<string, unknown>) => Promise<unknown>;
};

const IOS_PRODUCT_ID = String(import.meta.env.VITE_IAP_IOS_PRO_MONTHLY_PRODUCT_ID ?? '').trim();
const ANDROID_PRODUCT_ID = String(import.meta.env.VITE_IAP_ANDROID_PRO_MONTHLY_PRODUCT_ID ?? '').trim();
const IOS_APP_STORE_APP_ID = String(import.meta.env.VITE_IOS_APP_STORE_APP_ID ?? '').trim();
const ANDROID_PACKAGE_ID = String(
  import.meta.env.VITE_ANDROID_APP_PACKAGE_ID ?? 'com.swipewish.app'
).trim();

function getMobileStorePlatform(): MobileStorePlatform | null {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') return platform;
  return null;
}

function resolveNativePurchasePlugin(): DynamicPurchasePlugin | null {
  const plugins = (Capacitor as any)?.Plugins ?? {};
  return (
    (plugins.Purchases as DynamicPurchasePlugin | undefined)
    ?? (plugins.InAppPurchase as DynamicPurchasePlugin | undefined)
    ?? (plugins.InAppPurchase2 as DynamicPurchasePlugin | undefined)
    ?? null
  );
}

function buildStoreUrl(platform: MobileStorePlatform): string {
  if (platform === 'ios') {
    if (IOS_APP_STORE_APP_ID) {
      return `https://apps.apple.com/app/id${encodeURIComponent(IOS_APP_STORE_APP_ID)}`;
    }
    return 'https://apps.apple.com/account/subscriptions';
  }

  if (ANDROID_PRODUCT_ID) {
    const sku = encodeURIComponent(ANDROID_PRODUCT_ID);
    const pkg = encodeURIComponent(ANDROID_PACKAGE_ID || 'com.swipewish.app');
    return `https://play.google.com/store/account/subscriptions?sku=${sku}&package=${pkg}`;
  }
  return 'https://play.google.com/store/account/subscriptions';
}

function openExternalUrl(url: string) {
  if (typeof window === 'undefined') {
    throw new Error('Browser environment is not available');
  }

  try {
    const popup = window.open(url, '_blank', 'noopener,noreferrer');
    if (!popup) {
      window.location.assign(url);
    }
    return;
  } catch {
    window.location.assign(url);
  }
}

async function tryNativePluginPurchase(platform: MobileStorePlatform): Promise<boolean> {
  const plugin = resolveNativePurchasePlugin();
  if (!plugin) return false;

  const productId = platform === 'ios' ? IOS_PRODUCT_ID : ANDROID_PRODUCT_ID;
  if (!productId) return false;

  const payload = {
    productId,
    sku: productId,
    type: 'subs',
  };

  if (typeof plugin.subscribe === 'function') {
    await plugin.subscribe(payload);
    return true;
  }
  if (typeof plugin.purchase === 'function') {
    await plugin.purchase(payload);
    return true;
  }
  if (typeof plugin.buy === 'function') {
    await plugin.buy(payload);
    return true;
  }
  return false;
}

export const subscriptionService = {
  /** Piano effettivo dell'utente. Passa userId per evitare una chiamata extra a getSession(). */
  async myPlan(userId?: string): Promise<UserPlan | null> {
    const me = userId ?? (await supabase.auth.getSession()).data.session?.user?.id;
    if (!me) return null;
    const { data, error } = await supabase
      .from('v_user_plan')
      .select('*')
      .eq('user_id', me)
      .maybeSingle();
    if (error) throw error;
    return data as UserPlan | null;
  },

  async mySubscription(userId?: string): Promise<Subscription | null> {
    const me = userId ?? (await supabase.auth.getSession()).data.session?.user?.id;
    if (!me) return null;
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', me)
      .in('status', ['active', 'trialing', 'past_due', 'canceled'])
      .order('current_period_end', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return (data as Subscription) ?? null;
  },

  /** Avvia una subscription PayPal. */
  async createPaypalSubscription(): Promise<{ approveUrl: string; subscriptionId: string }> {
    const { data, error } = await supabase.functions.invoke('create-paypal-subscription', {});
    if (error) throw error;
    return data as { approveUrl: string; subscriptionId: string };
  },

  getMobileStorePlatform,

  /** Avvia acquisto Pro mensile su store mobile (best effort). */
  async startMobileStoreSubscription(): Promise<MobileStoreStartResult> {
    const platform = getMobileStorePlatform();
    if (!platform) {
      throw new Error('MOBILE_STORE_ONLY');
    }

    const usedNativePlugin = await tryNativePluginPurchase(platform).catch(() => false);
    if (usedNativePlugin) {
      return { platform, mode: 'native-plugin' };
    }

    const targetUrl = buildStoreUrl(platform);
    openExternalUrl(targetUrl);
    return { platform, mode: 'store-link', targetUrl };
  },

  /** Richiede annullamento abbonamento (provider-ruled; può ritornare manual_required). */
  async cancelMySubscription(): Promise<CancelSubscriptionResult> {
    const { data, error } = await supabase.functions.invoke('cancel-subscription', { body: {} });
    if (error) {
      const response = (error as any)?.context;
      if (response && typeof response.json === 'function') {
        const payload = await response.json().catch(() => null);
        if (payload) {
          const err = new Error(payload?.error ?? error.message ?? 'Subscription cancel failed');
          (err as any).code = payload?.code;
          throw err;
        }
      }
      throw error;
    }
    return (data as CancelSubscriptionResult) ?? { ok: false, provider: 'manual' };
  },
};
